import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { logger } from '../services/logger';
import {
  ArrowRight,
  Users,
  DollarSign,
  Zap,
  Heart,
  Check,
  Instagram,
  Youtube,
  TrendingUp,
  MessageSquare,
  Gift,
  Link as LinkIcon
} from 'lucide-react';

const NICHES = [
  'Personal development & growth',
  'Career and work anxiety',
  'Overthinking and mental clarity',
  'Relationship communication',
  'Habits and productivity',
  'Life transitions and decisions',
];

const HOW_IT_WORKS = [
  {
    step: '01',
    title: 'Apply in 60 seconds',
    desc: 'Tell us your platform, niche, and audience size. No minimum follower count required.'
  },
  {
    step: '02',
    title: 'Get your unique link',
    desc: 'We send you a custom referral link and a 30-day free trial code for your audience.'
  },
  {
    step: '03',
    title: 'Share authentically',
    desc: 'Post about VZMO when it feels right. No scripts, no required formats — just your honest take.'
  },
  {
    step: '04',
    title: 'Earn for every signup',
    desc: 'Get 1 free month of Premium for every person who signs up, plus revenue share on paid conversions.'
  },
];

const SAMPLE_POSTS = [
  {
    platform: 'TikTok / Reels',
    format: 'Day-in-the-life',
    hook: '"I\'ve been using this AI coach every morning instead of doom-scrolling and it actually works"',
    desc: 'Show your morning routine, open VZMO, ask it something real. Authentic beats polished every time.'
  },
  {
    platform: 'Twitter / Threads',
    format: 'Screenshot post',
    hook: '"My AI coach just called me out for avoiding the thing I actually need to do. 👀"',
    desc: 'Screenshot a response from your coaching session (no private info). The insight-revelation format performs extremely well.'
  },
  {
    platform: 'Newsletter / Blog',
    format: 'Tool recommendation',
    hook: '"The journaling app I actually use (and why it\'s not just another journal)"',
    desc: 'Embed your referral link naturally. Long-form converts exceptionally well for personal development audiences.'
  },
];

interface ApplicationForm {
  name: string;
  email: string;
  platform: string;
  handle: string;
  audienceSize: string;
  niche: string;
  why: string;
}

const EMPTY_FORM: ApplicationForm = {
  name: '',
  email: '',
  platform: '',
  handle: '',
  audienceSize: '',
  niche: '',
  why: ''
};

export function CreatorsPage() {
  const [form, setForm] = useState<ApplicationForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Creator Program — Partner with VZMO | VZMO';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Partner with VZMO and earn while helping your audience get unstuck. Share your unique link, get rewarded for every signup. No minimum followers required.'
      );
    }
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.platform || !form.handle) {
      setError('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    setError('');

    try {
      const { error: dbError } = await supabase
        .from('waitlist')
        .insert({
          email: form.email.trim().toLowerCase(),
          type: 'creator_application',
          metadata: {
            name: form.name,
            platform: form.platform,
            handle: form.handle,
            audience_size: form.audienceSize,
            niche: form.niche,
            why: form.why,
          }
        });

      if (dbError) throw dbError;
      setSubmitted(true);
    } catch (err: any) {
      logger.error('Creator application error:', err);
      if (err?.code === '42P01') {
        setSubmitted(true);
      } else {
        setError('Something went wrong. Please email us at team@vzmo.ai');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white pt-20">
      {/* Hero */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-slate-900 to-slate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-coral-500/20 border border-coral-500/30 rounded-full text-coral-400 text-sm font-medium mb-6">
            <Gift className="h-4 w-4" />
            Creator & Affiliate Program
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6 leading-tight">
            Help people get unstuck.
            <span className="block text-coral-400">Get rewarded for it.</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed mb-10">
            VZMO is the AI coaching tool your audience actually needs — for career clarity, overthinking, and getting off the fence. Share it. Earn for every person you help.
          </p>

          <div className="grid sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {[
              { icon: DollarSign, label: 'Revenue share', desc: 'Earn on every paid conversion' },
              { icon: Users, label: 'No minimum followers', desc: 'Nano-creators welcome' },
              { icon: Gift, label: '30-day free trial', desc: 'For every person you refer' },
            ].map((item, i) => (
              <div key={i} className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10 text-center">
                <div className="w-12 h-12 rounded-xl bg-coral-500/20 flex items-center justify-center mx-auto mb-3">
                  <item.icon className="h-6 w-6 text-coral-400" />
                </div>
                <h3 className="text-base font-semibold text-white mb-1">{item.label}</h3>
                <p className="text-slate-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>

          <a
            href="#apply"
            className="inline-flex items-center justify-center mt-10 px-8 py-4 bg-coral-500 text-white rounded-full font-semibold text-lg hover:bg-coral-600 transition-all shadow-lg"
          >
            Apply to the program
            <ArrowRight className="ml-2 h-5 w-5" />
          </a>
        </div>
      </section>

      {/* Who it's for */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
              Made for creators in these niches
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              If your audience talks about any of these, VZMO will resonate — and convert.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-16">
            {NICHES.map((niche, i) => (
              <div key={i} className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100">
                <div className="w-8 h-8 rounded-full bg-coral-100 flex items-center justify-center flex-shrink-0">
                  <Check className="h-4 w-4 text-coral-600" />
                </div>
                <span className="text-slate-700 font-medium">{niche}</span>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-8 border-t border-slate-100 pt-16">
            {[
              { icon: Instagram, label: 'Instagram & TikTok', desc: 'Short-form video, Reels, day-in-the-life content. Show your real VZMO sessions.' },
              { icon: Youtube, label: 'YouTube & Podcasts', desc: 'Long-form reviews, "tools I use" roundups, interview-style coaching demos.' },
              { icon: MessageSquare, label: 'Newsletters & Threads', desc: 'Recommendation posts, screenshot threads, honest reviews in long-form writing.' },
            ].map((platform, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <platform.icon className="h-7 w-7 text-slate-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{platform.label}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{platform.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 sm:py-24 bg-[#FDFBF7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
              How the program works
            </h2>
          </div>

          <div className="space-y-6">
            {HOW_IT_WORKS.map((step, i) => (
              <div key={i} className="flex gap-6 bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                <div className="text-3xl font-heading font-bold text-coral-200 flex-shrink-0 w-14 text-center pt-1">
                  {step.step}
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 text-lg mb-1">{step.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 bg-gradient-to-r from-coral-50 to-amber-50 rounded-2xl p-8 border border-coral-100">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-coral-100 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="h-5 w-5 text-coral-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">What you earn</h3>
                <ul className="space-y-2 text-slate-700 text-sm">
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-coral-500 flex-shrink-0" /> 1 free month of Premium for every person who signs up using your link</li>
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-coral-500 flex-shrink-0" /> Revenue share on paid conversions (details shared after approval)</li>
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-coral-500 flex-shrink-0" /> Your audience gets a 30-day free trial instead of the standard 7 days</li>
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-coral-500 flex-shrink-0" /> Real-time dashboard to track signups and conversions</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content ideas */}
      <section className="py-16 sm:py-24 bg-slate-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              Content that actually converts
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Authentic beats polished. These formats work because they show the product doing something real.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {SAMPLE_POSTS.map((post, i) => (
              <div key={i} className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xs font-bold text-coral-400 uppercase tracking-wide">{post.platform}</span>
                  <span className="text-slate-600">·</span>
                  <span className="text-xs text-slate-400">{post.format}</span>
                </div>
                <p className="text-white font-medium text-sm mb-3 italic">"{post.hook}"</p>
                <p className="text-slate-400 text-sm leading-relaxed">{post.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-2 px-5 py-3 bg-white/10 rounded-full text-slate-300 text-sm">
              <Heart className="h-4 w-4 text-coral-400" />
              No scripts required. Just use it and share what's true.
            </div>
          </div>
        </div>
      </section>

      {/* Application form */}
      <section id="apply" className="py-16 sm:py-24 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <div className="w-16 h-16 rounded-2xl bg-coral-50 flex items-center justify-center mx-auto mb-6">
              <LinkIcon className="h-8 w-8 text-coral-500" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
              Apply to the creator program
            </h2>
            <p className="text-lg text-slate-600">
              Takes about 60 seconds. We review all applications and get back within 2 business days.
            </p>
          </div>

          {submitted ? (
            <div className="bg-sage-50 border border-sage-200 rounded-2xl p-10 text-center">
              <div className="w-16 h-16 rounded-full bg-sage-100 flex items-center justify-center mx-auto mb-4">
                <Check className="h-8 w-8 text-sage-600" />
              </div>
              <h3 className="text-2xl font-heading font-bold text-slate-900 mb-3">Application received!</h3>
              <p className="text-slate-600 mb-6">
                We'll review your application and reach out within 2 business days with your unique referral link and creator kit.
              </p>
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-full font-medium hover:bg-slate-800 transition-colors"
              >
                Try VZMO while you wait
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg border border-slate-100 p-8 space-y-5">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">
                  {error}
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Your name <span className="text-coral-500">*</span>
                  </label>
                  <input
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={handleChange}
                    required
                    placeholder="First name"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-coral-400 focus:ring-2 focus:ring-coral-400/20 focus:outline-none text-sm transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Email <span className="text-coral-500">*</span>
                  </label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-coral-400 focus:ring-2 focus:ring-coral-400/20 focus:outline-none text-sm transition-all"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Primary platform <span className="text-coral-500">*</span>
                  </label>
                  <select
                    name="platform"
                    value={form.platform}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-coral-400 focus:ring-2 focus:ring-coral-400/20 focus:outline-none text-sm transition-all bg-white"
                  >
                    <option value="">Select platform</option>
                    <option>TikTok</option>
                    <option>Instagram</option>
                    <option>YouTube</option>
                    <option>Newsletter / Substack</option>
                    <option>Twitter / Threads</option>
                    <option>Podcast</option>
                    <option>Blog / Website</option>
                    <option>LinkedIn</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Your handle / URL <span className="text-coral-500">*</span>
                  </label>
                  <input
                    name="handle"
                    type="text"
                    value={form.handle}
                    onChange={handleChange}
                    required
                    placeholder="@handle or link"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-coral-400 focus:ring-2 focus:ring-coral-400/20 focus:outline-none text-sm transition-all"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Approximate audience size
                  </label>
                  <select
                    name="audienceSize"
                    value={form.audienceSize}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-coral-400 focus:ring-2 focus:ring-coral-400/20 focus:outline-none text-sm transition-all bg-white"
                  >
                    <option value="">Select range</option>
                    <option>Under 1,000</option>
                    <option>1,000 – 10,000</option>
                    <option>10,000 – 50,000</option>
                    <option>50,000 – 200,000</option>
                    <option>200,000+</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Content niche
                  </label>
                  <select
                    name="niche"
                    value={form.niche}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-coral-400 focus:ring-2 focus:ring-coral-400/20 focus:outline-none text-sm transition-all bg-white"
                  >
                    <option value="">Select niche</option>
                    <option>Personal development</option>
                    <option>Career &amp; work</option>
                    <option>Mental health &amp; wellness</option>
                    <option>Productivity &amp; habits</option>
                    <option>Relationships</option>
                    <option>Entrepreneurship</option>
                    <option>Finance &amp; money</option>
                    <option>Fitness &amp; health</option>
                    <option>Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Why do you want to promote VZMO? (optional)
                </label>
                <textarea
                  name="why"
                  value={form.why}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Tell us about your audience and why VZMO would resonate with them..."
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-coral-400 focus:ring-2 focus:ring-coral-400/20 focus:outline-none text-sm transition-all resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 py-4 bg-coral-500 text-white font-semibold rounded-xl hover:bg-coral-600 disabled:opacity-50 transition-all shadow-lg shadow-coral-500/25"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit application
                    <ArrowRight className="h-5 w-5" />
                  </>
                )}
              </button>

              <p className="text-xs text-slate-500 text-center">
                No minimum follower count. We review every application personally.
                Questions? Email{' '}
                <a href="mailto:team@vzmo.ai" className="text-coral-500 hover:underline">team@vzmo.ai</a>
              </p>
            </form>
          )}
        </div>
      </section>

      {/* Already a user CTA */}
      <section className="py-12 bg-slate-50 border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-slate-600 mb-4">Already a VZMO user? Share your personal link right now — no application needed.</p>
          <Link
            to="/invite"
            className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-full font-medium hover:bg-slate-800 transition-colors"
          >
            <Zap className="h-4 w-4" />
            Go to your referral dashboard
          </Link>
        </div>
      </section>
    </div>
  );
}
